import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from "@tanstack/react-router";
import { lazy } from "react";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { LazyPage } from "@/shared/ui/LazyPage";

const RootLayout = lazy(() =>
  import("./layout/RootLayout").then((module) => ({
    default: module.RootLayout,
  })),
);
const ErrorBoundary = lazy(() =>
  import("./error/ErrorBoundary").then((module) => ({
    default: module.ErrorBoundary,
  })),
);
const AuthLayout = lazy(() =>
  import("./layout/AuthLayout").then((module) => ({
    default: module.AuthLayout,
  })),
);

const LoginPage = lazy(() =>
  import("@/modules/auth/presentation/pages/LoginPage").then((module) => ({
    default: module.LoginPage,
  })),
);
const AuthenticatedLayout = lazy(() =>
  import("./layout/AuthenticatedLayout").then((module) => ({
    default: module.AuthenticatedLayout,
  })),
);
const DashboardPage = lazy(() =>
  import("@/modules/dashboard/presentation/pages/DashboardPage").then(
    (module) => ({
      default: module.DashboardPage,
    }),
  ),
);
const RolesPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RolesPage").then((module) => ({
    default: module.RolesPage,
  })),
);
const RoleCreatePage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleCreatePage").then(
    (module) => ({
      default: module.RoleCreatePage,
    }),
  ),
);
const RoleEditPage = lazy(() =>
  import("@/modules/roles/presentation/pages/RoleEditPage").then((module) => ({
    default: module.RoleEditPage,
  })),
);
const AuditPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditPage").then((module) => ({
    default: module.AuditPage,
  })),
);
const AuditDetailPage = lazy(() =>
  import("@/modules/audit/presentation/pages/AuditDetailPage").then(
    (module) => ({
      default: module.AuditDetailPage,
    }),
  ),
);
const UsersPage = lazy(() =>
  import("@/modules/users/presentation/pages/UsersPage").then((module) => ({
    default: module.UsersPage,
  })),
);
const UserCreatePage = lazy(() =>
  import("@/modules/users/presentation/pages/UserCreatePage").then(
    (module) => ({
      default: module.UserCreatePage,
    }),
  ),
);
const UserEditPage = lazy(() =>
  import("@/modules/users/presentation/pages/UserEditPage").then((module) => ({
    default: module.UserEditPage,
  })),
);
const ShopsPage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopsPage").then((module) => ({
    default: module.ShopsPage,
  })),
);
const ShopCreatePage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopCreatePage").then(
    (module) => ({
      default: module.ShopCreatePage,
    }),
  ),
);
const ShopDetailPage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopDetailPage").then(
    (module) => ({
      default: module.ShopDetailPage,
    }),
  ),
);
const ShopVerifyPage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopVerifyPage").then(
    (module) => ({
      default: module.ShopVerifyPage,
    }),
  ),
);
const ShopBranchesPage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopBranchesPage").then(
    (module) => ({
      default: module.ShopBranchesPage,
    }),
  ),
);
const ShopProductsPage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopProductsPage").then(
    (module) => ({
      default: module.ShopProductsPage,
    }),
  ),
);
const ShopPerformancePage = lazy(() =>
  import("@/modules/shops/presentation/pages/ShopPerformancePage").then(
    (module) => ({
      default: module.ShopPerformancePage,
    }),
  ),
);
const MechanicOnboardingPage = lazy(() =>
  import("@/modules/mechanics/presentation/pages/MechanicOnboardingPage").then(
    (module) => ({
      default: module.MechanicOnboardingPage,
    }),
  ),
);
const MechanicDetailPage = lazy(() =>
  import("@/modules/mechanics/presentation/pages/MechanicDetailPage").then(
    (module) => ({
      default: module.MechanicDetailPage,
    }),
  ),
);
const MechanicPerformancePage = lazy(() =>
  import("@/modules/mechanics/presentation/pages/MechanicPerformancePage").then(
    (module) => ({
      default: module.MechanicPerformancePage,
    }),
  ),
);
const FeesPage = lazy(() =>
  import("@/modules/fees/presentation/pages/FeesPage").then((module) => ({
    default: module.FeesPage,
  })),
);
const CategoriesPage = lazy(() =>
  import("@/modules/categories/presentation/pages/CategoriesPage").then(
    (module) => ({ default: module.CategoriesPage }),
  ),
);
const CatalogProductsPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogProductsPage").then(
    (module) => ({ default: module.CatalogProductsPage }),
  ),
);
const CatalogProductCreatePage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogProductCreatePage").then(
    (module) => ({
      default: module.CatalogProductCreatePage,
    }),
  ),
);
const CatalogProductEditPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogProductEditPage").then(
    (module) => ({
      default: module.CatalogProductEditPage,
    }),
  ),
);
const CatalogProductDetailPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogProductDetailPage").then(
    (module) => ({ default: module.CatalogProductDetailPage }),
  ),
);
const CatalogBrandsPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogBrandsPage").then(
    (module) => ({ default: module.CatalogBrandsPage }),
  ),
);
const CatalogPendingFitmentPage = lazy(() =>
  import(
    "@/modules/catalog/presentation/pages/CatalogPendingFitmentPage"
  ).then((module) => ({
    default: module.CatalogPendingFitmentPage,
  })),
);
const CatalogCandidatesPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogCandidatesPage").then(
    (module) => ({ default: module.CatalogCandidatesPage }),
  ),
);
const CatalogVehiclesPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogVehiclesPage").then(
    (module) => ({ default: module.CatalogVehiclesPage }),
  ),
);
const CatalogVehicleImportPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogVehicleImportPage").then(
    (module) => ({ default: module.CatalogVehicleImportPage }),
  ),
);
const CatalogVehicleMakeDetailPage = lazy(() =>
  import(
    "@/modules/catalog/presentation/pages/CatalogVehicleMakeDetailPage"
  ).then((module) => ({
    default: module.CatalogVehicleMakeDetailPage,
  })),
);
const CatalogVehicleModelDetailPage = lazy(() =>
  import(
    "@/modules/catalog/presentation/pages/CatalogVehicleModelDetailPage"
  ).then((module) => ({
    default: module.CatalogVehicleModelDetailPage,
  })),
);
const CatalogVehicleTrimEPCPage = lazy(() =>
  import("@/modules/catalog/presentation/pages/CatalogVehicleTrimEPCPage").then(
    (module) => ({
      default: module.CatalogVehicleTrimEPCPage,
    }),
  ),
);
const ListingsPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/ListingsPage").then(
    (module) => ({ default: module.ListingsPage }),
  ),
);
const CreateListingPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/CreateListingPage").then(
    (module) => ({ default: module.CreateListingPage }),
  ),
);
const ListingDetailPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/ListingDetailPage").then(
    (module) => ({ default: module.ListingDetailPage }),
  ),
);
const ListingProductDetailPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/ListingProductDetailPage").then(
    (module) => ({ default: module.ListingProductDetailPage }),
  ),
);
const ReportsPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/ReportsPage").then(
    (module) => ({ default: module.ReportsPage }),
  ),
);
const ReportDetailPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/ReportDetailPage").then(
    (module) => ({ default: module.ReportDetailPage }),
  ),
);
const ModerationHistoryPage = lazy(() =>
  import("@/modules/moderation/presentation/pages/ModerationHistoryPage").then(
    (module) => ({ default: module.ModerationHistoryPage }),
  ),
);
const DisputesPage = lazy(() =>
  import("@/modules/disputes/presentation/pages/DisputesPage").then(
    (module) => ({ default: module.DisputesPage }),
  ),
);
const DisputeDetailPage = lazy(() =>
  import("@/modules/disputes/presentation/pages/DisputeDetailPage").then(
    (module) => ({ default: module.DisputeDetailPage }),
  ),
);
const WalletOverviewPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletOverviewPage").then(
    (module) => ({
      default: module.WalletOverviewPage,
    }),
  ),
);
const WalletAccountsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletAccountsPage").then(
    (module) => ({
      default: module.WalletAccountsPage,
    }),
  ),
);
const WalletTransactionsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletTransactionsPage").then(
    (module) => ({
      default: module.WalletTransactionsPage,
    }),
  ),
);
const TopupRequestsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/TopupRequestsPage").then(
    (module) => ({
      default: module.TopupRequestsPage,
    }),
  ),
);
const TopupDetailPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/TopupDetailPage").then(
    (module) => ({
      default: module.TopupDetailPage,
    }),
  ),
);
const KycRequestsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/KycRequestsPage").then(
    (module) => ({
      default: module.KycRequestsPage,
    }),
  ),
);
const KycRequestDetailPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/KycRequestDetailPage").then(
    (module) => ({
      default: module.KycRequestDetailPage,
    }),
  ),
);
const RefundsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/RefundsPage").then((module) => ({
    default: module.RefundsPage,
  })),
);
const RefundCreatePage = lazy(() =>
  import("@/modules/wallet/presentation/pages/RefundCreatePage").then(
    (module) => ({
      default: module.RefundCreatePage,
    }),
  ),
);
const PayoutsPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/PayoutsPage").then((module) => ({
    default: module.PayoutsPage,
  })),
);
const PayoutCreatePage = lazy(() =>
  import("@/modules/wallet/presentation/pages/PayoutCreatePage").then(
    (module) => ({
      default: module.PayoutCreatePage,
    }),
  ),
);
const PayoutDetailPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/PayoutDetailPage").then(
    (module) => ({
      default: module.PayoutDetailPage,
    }),
  ),
);
const WalletFeesPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletFeesPage").then(
    (module) => ({
      default: module.WalletFeesPage,
    }),
  ),
);
const WalletFeeCreatePage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletFeeCreatePage").then(
    (module) => ({
      default: module.WalletFeeCreatePage,
    }),
  ),
);
const WalletFeeEditPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/WalletFeeEditPage").then(
    (module) => ({
      default: module.WalletFeeEditPage,
    }),
  ),
);
const CompliancePage = lazy(() =>
  import("@/modules/wallet/presentation/pages/CompliancePage").then(
    (module) => ({
      default: module.CompliancePage,
    }),
  ),
);
const ReconciliationPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/ReconciliationPage").then(
    (module) => ({
      default: module.ReconciliationPage,
    }),
  ),
);
const ReconciliationImportPage = lazy(() =>
  import("@/modules/wallet/presentation/pages/ReconciliationImportPage").then(
    (module) => ({
      default: module.ReconciliationImportPage,
    }),
  ),
);
const NotificationsPage = lazy(() =>
  import("@/modules/notifications/presentation/pages/NotificationsPage").then(
    (module) => ({
      default: module.NotificationsPage,
    }),
  ),
);
const ProfilePage = lazy(() =>
  import("@/modules/auth/presentation/pages/ProfilePage").then((module) => ({
    default: module.ProfilePage,
  })),
);
const Forbidden = lazy(() =>
  import("./error/Forbidden").then((module) => ({
    default: module.Forbidden,
  })),
);
const DevelopmentStatusPage = lazy(() =>
  import(
    "@/modules/development-status/presentation/pages/DevelopmentStatusPage"
  ).then((module) => ({
    default: module.DevelopmentStatusPage,
  })),
);

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: ErrorBoundary,
  beforeLoad: ({ location }) => {
    if (
      location.pathname === "/" ||
      location.pathname === "/app" ||
      location.pathname === "/app/"
    ) {
      throw redirect({ to: "/app/dashboard", replace: true });
    }
  },
});

const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/auth",
  component: AuthLayout,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: "/login",
  component: LoginPage,
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app",
  component: AuthenticatedLayout,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/dashboard",
  component: () => (
    <LazyPage>
      <DashboardPage />
    </LazyPage>
  ),
});

const rolesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles",
  component: () => (
    <RequirePermissions all={["roles:read"]}>
      <LazyPage>
        <RolesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/create",
  component: () => (
    <RequirePermissions all={["roles:read"]} any={["roles:create"]}>
      <LazyPage>
        <RoleCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const roleEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/roles/$id/edit",
  component: () => (
    <RequirePermissions
      all={["roles:read"]}
      any={["roles:update", "roles:delete"]}
    >
      <LazyPage>
        <RoleEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const auditDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/audit/$id",
  component: () => (
    <RequirePermissions all={["audit:read"]}>
      <LazyPage>
        <AuditDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: () => (
    <RequirePermissions all={["users:read"]}>
      <LazyPage>
        <UsersPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/create",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:create"]}>
      <LazyPage>
        <UserCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const userEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users/$id/edit",
  component: () => (
    <RequirePermissions all={["users:read"]} any={["users:update"]}>
      <LazyPage>
        <UserEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops",
  component: () => (
    <RequirePermissions all={["shops:list"]}>
      <LazyPage>
        <ShopsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops/create",
  component: () => (
    <RequirePermissions all={["shops:list"]} any={["shops:create"]}>
      <LazyPage>
        <ShopCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops/$id",
  component: () => (
    <RequirePermissions all={["shops:list"]} any={["shops:read"]}>
      <LazyPage>
        <ShopDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopVerifyRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops/$id/verify",
  component: () => (
    <RequirePermissions
      all={["shops:list"]}
      any={["shops:verify", "shops:read"]}
    >
      <LazyPage>
        <ShopVerifyPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopBranchesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops/$id/branches",
  component: () => (
    <RequirePermissions any={["shops:read", "branches:list"]}>
      <LazyPage>
        <ShopBranchesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopProductsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops/$id/products",
  component: () => (
    <RequirePermissions all={["shops:list"]} any={["catalog:read"]}>
      <LazyPage>
        <ShopProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const shopPerformanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/shops/performance",
  component: () => (
    <RequirePermissions all={["shops:read", "reports:read"]}>
      <LazyPage>
        <ShopPerformancePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const mechanicsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mechanics",
  component: () => (
    <RequirePermissions all={["mechanics:list"]}>
      <LazyPage>
        <MechanicOnboardingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const mechanicDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mechanics/$id",
  component: () => (
    <RequirePermissions all={["mechanics:list"]} any={["mechanics:read"]}>
      <LazyPage>
        <MechanicDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const mechanicPerformanceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/mechanics/$id/performance",
  component: () => (
    <RequirePermissions all={["mechanics:list"]} any={["mechanics:read"]}>
      <LazyPage>
        <MechanicPerformancePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const feesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/fees",
  component: () => (
    <RequirePermissions all={["fee_rules:list"]}>
      <LazyPage>
        <FeesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const categoriesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/categories",
  component: () => (
    <RequirePermissions all={["categories:list"]}>
      <LazyPage>
        <CategoriesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogProductsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/create",
  component: () => (
    <RequirePermissions all={["catalog:read"]} any={["catalog:create"]}>
      <LazyPage>
        <CatalogProductCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/$id/edit",
  component: () => (
    <RequirePermissions all={["catalog:read"]} any={["catalog:update"]}>
      <LazyPage>
        <CatalogProductEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/$id",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogProductDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogBrandsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/brands",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogBrandsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogPendingFitmentRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/pending-fitment",
  validateSearch: (s: Record<string, unknown>) => ({
    limit: Number(s.limit) || 20,
    offset: Number(s.offset) || 0,
  }),
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogPendingFitmentPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogCandidatesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/candidates",
  validateSearch: (s: Record<string, unknown>) => ({
    limit: Number(s.limit) || 20,
    offset: Number(s.offset) || 0,
  }),
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogCandidatesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogVehiclesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/vehicles",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogVehiclesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogVehicleImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/vehicles/import",
  component: () => (
    <RequirePermissions all={["catalog:create"]}>
      <LazyPage>
        <CatalogVehicleImportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogVehicleMakeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/vehicles/$makeId",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogVehicleMakeDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogVehicleModelDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/vehicles/$makeId/models/$modelId",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogVehicleModelDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const catalogVehicleConfigurationEpcRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/catalog/vehicles/$makeId/models/$modelId/configurations/$configurationId/epc",
  component: () => (
    <RequirePermissions all={["catalog:read"]}>
      <LazyPage>
        <CatalogVehicleTrimEPCPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const moderationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation",
  validateSearch: (s: Record<string, unknown>) => ({
    limit: Number(s.limit) || 20,
    offset: Number(s.offset) || 0,
    q: typeof s.q === "string" ? s.q : undefined,
    recentDays: s.recentDays != null ? Number(s.recentDays) : undefined,
    sort: s.sort,
    filters: s.filters,
  }),
  component: () => (
    <RequirePermissions all={["listings:list"]}>
      <LazyPage>
        <ListingsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const createListingRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation/listings/create",
  component: () => (
    <RequirePermissions all={["listings:update"]}>
      <LazyPage>
        <CreateListingPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const listingProductDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation/listings/products/$catalogProductId",
  component: () => (
    <RequirePermissions all={["listings:read"]}>
      <LazyPage>
        <ListingProductDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const listingDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation/listings/$id",
  component: () => (
    <RequirePermissions all={["listings:read"]}>
      <LazyPage>
        <ListingDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation/reports",
  component: () => (
    <RequirePermissions all={["reports:list"]}>
      <LazyPage>
        <ReportsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reportDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation/reports/$id",
  component: () => (
    <RequirePermissions all={["reports:read"]}>
      <LazyPage>
        <ReportDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const moderationHistoryRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/moderation/history",
  component: () => (
    <RequirePermissions all={["moderation:history"]}>
      <LazyPage>
        <ModerationHistoryPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const disputesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/disputes",
  component: () => (
    <RequirePermissions all={["disputes:list"]}>
      <LazyPage>
        <DisputesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const disputeDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/disputes/$id",
  component: () => (
    <RequirePermissions all={["disputes:read"]}>
      <LazyPage>
        <DisputeDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletOverviewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet",
  component: () => (
    <RequirePermissions all={["wallet:overview"]}>
      <LazyPage>
        <WalletOverviewPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletAccountsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/accounts",
  component: () => (
    <RequirePermissions all={["wallet:list"]}>
      <LazyPage>
        <WalletAccountsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletTransactionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/transactions",
  component: () => (
    <RequirePermissions all={["wallet:transactions"]}>
      <LazyPage>
        <WalletTransactionsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const topupRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/topup",
  component: () => (
    <RequirePermissions all={["top_up:list"]}>
      <LazyPage>
        <TopupRequestsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const topupDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/topup/$id",
  component: () => (
    <RequirePermissions all={["top_up:list"]}>
      <LazyPage>
        <TopupDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const kycRequestsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/kyc-requests",
  component: () => (
    <RequirePermissions all={["compliance:read"]}>
      <LazyPage>
        <KycRequestsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const kycRequestDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/kyc-requests/$id",
  component: () => (
    <RequirePermissions all={["compliance:read"]}>
      <LazyPage>
        <KycRequestDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const refundsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/refunds",
  component: () => (
    <RequirePermissions all={["refund:list"]}>
      <LazyPage>
        <RefundsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const refundCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/refunds/create",
  component: () => (
    <RequirePermissions all={["refund:create"]}>
      <LazyPage>
        <RefundCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const payoutsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/payouts",
  component: () => (
    <RequirePermissions all={["payout:list"]}>
      <LazyPage>
        <PayoutsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const payoutCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/payouts/create",
  component: () => (
    <RequirePermissions all={["payout:create"]}>
      <LazyPage>
        <PayoutCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const payoutDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/payouts/$id",
  component: () => (
    <RequirePermissions all={["payout:read"]}>
      <LazyPage>
        <PayoutDetailPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletFeesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/fees",
  component: () => (
    <RequirePermissions all={["wallet_fee:list"]}>
      <LazyPage>
        <WalletFeesPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletFeeCreateRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/fees/create",
  component: () => (
    <RequirePermissions all={["wallet_fee:create"]}>
      <LazyPage>
        <WalletFeeCreatePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const walletFeeEditRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/fees/$id/edit",
  component: () => (
    <RequirePermissions all={["wallet_fee:update"]}>
      <LazyPage>
        <WalletFeeEditPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const complianceRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/compliance",
  component: () => (
    <RequirePermissions all={["compliance:read"]}>
      <LazyPage>
        <CompliancePage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reconciliationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/reconciliation",
  component: () => (
    <RequirePermissions all={["reconciliation:list"]}>
      <LazyPage>
        <ReconciliationPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const reconciliationImportRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/wallet/reconciliation/import",
  component: () => (
    <RequirePermissions all={["reconciliation:list"]}>
      <LazyPage>
        <ReconciliationImportPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const notificationsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/notifications",
  validateSearch: (s: Record<string, unknown>) => ({
    limit: Number(s.limit) || 20,
    offset: Number(s.offset) || 0,
    type: typeof s.type === "string" && s.type ? s.type : undefined,
    unreadOnly: s.unreadOnly === true || s.unreadOnly === "true",
  }),
  component: () => (
    <RequirePermissions
      any={["top_up:list", "compliance:read", "suspicious:list"]}
    >
      <LazyPage>
        <NotificationsPage />
      </LazyPage>
    </RequirePermissions>
  ),
});

const profileRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/profile",
  component: () => (
    <LazyPage>
      <ProfilePage />
    </LazyPage>
  ),
});

const forbiddenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/errors/forbidden",
  component: () => (
    <LazyPage>
      <Forbidden />
    </LazyPage>
  ),
});

const developmentStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/development",
  component: () => (
    <LazyPage>
      <DevelopmentStatusPage />
    </LazyPage>
  ),
});

export const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([loginRoute]),
  appRoute.addChildren([
    dashboardRoute,
    rolesRoute,
    roleCreateRoute,
    roleEditRoute,
    usersRoute,
    userCreateRoute,
    userEditRoute,
    shopsRoute,
    shopCreateRoute,
    shopDetailRoute,
    shopVerifyRoute,
    shopBranchesRoute,
    shopProductsRoute,
    shopPerformanceRoute,
    mechanicsRoute,
    mechanicDetailRoute,
    mechanicPerformanceRoute,
    feesRoute,
    categoriesRoute,
    catalogRoute,
    catalogCreateRoute,
    catalogEditRoute,
    catalogDetailRoute,
    catalogBrandsRoute,
    catalogPendingFitmentRoute,
    catalogCandidatesRoute,
    catalogVehiclesRoute,
    catalogVehicleImportRoute,
    catalogVehicleMakeDetailRoute,
    catalogVehicleModelDetailRoute,
    catalogVehicleConfigurationEpcRoute,
    moderationRoute,
    createListingRoute,
    listingProductDetailRoute,
    listingDetailRoute,
    reportsRoute,
    reportDetailRoute,
    moderationHistoryRoute,
    disputesRoute,
    disputeDetailRoute,
    auditRoute,
    auditDetailRoute,
    walletOverviewRoute,
    walletAccountsRoute,
    walletTransactionsRoute,
    topupRequestsRoute,
    topupDetailRoute,
    kycRequestsRoute,
    kycRequestDetailRoute,
    refundsRoute,
    refundCreateRoute,
    payoutsRoute,
    payoutCreateRoute,
    payoutDetailRoute,
    walletFeesRoute,
    walletFeeCreateRoute,
    walletFeeEditRoute,
    complianceRoute,
    reconciliationRoute,
    reconciliationImportRoute,
    notificationsRoute,
    profileRoute,
  ]),
  forbiddenRoute,
  developmentStatusRoute,
]);
export const router = createRouter({ routeTree });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
